/**
 * EigenApp preview runtime (Lane Q6).
 * Contract: eigenify-ai/docs/EIGENAPP-PREVIEW-PROTOCOL.md
 *
 * Renders an App from a postMessage payload WITHOUT PocketBase, reusing the
 * SAME render path as the experimental app loader (routes/_/app/index.tsx):
 *
 *   experimental loader: core.experimental_getApp -> internalDriver fetches
 *   the experimental_app FILE from PocketBase -> @data-root/loader load(url)
 *   executes it as an ES module and reads the `app` export -> the
 *   AppWithEnvironment is pushed into core.app.apps -> AppRenderer renders.
 *
 *   preview: the bundle TEXT arrives via APP_PREVIEW -> executed as an ES
 *   module via a Blob URL dynamic import (the same execution semantics as
 *   load(), minus the network fetch) -> the AppWithEnvironment is pushed into
 *   core.app.apps directly (NO internalDriver, NO PocketBase) -> the same
 *   AppRenderer renders it.
 *
 * SECURITY (contract section 4):
 * - No PocketBase: the App comes from the message; only core.app.apps is
 *   seeded; core.loadApp registers LOCAL data drivers only.
 * - dataProvider is FORCED to "Local" on every table regardless of what the
 *   app declares ("PostgresLite" may be honored once Q8 lands the driver).
 * - No session: core.fromToken is never called and core.save() is never
 *   called. A synthetic in-memory user object (no token, nothing persisted)
 *   is set so in-app row mutations (which stamp deltas with a userId) work.
 */
import type {
  App,
  AppSpecifier,
  DatabaseMap,
  Manifest,
  User,
} from "@data-root/types";
import { core } from "@/index";
import { changeThemeColor } from "@/utils";
import { v4 as uuidv4 } from "uuid";
import { MAX_BUNDLE_TEXT_CHARS } from "@/services/postMessage/handlers/AppPreviewHandler";

export interface RegisteredPreview {
  appSpecifier: AppSpecifier;
  app: App;
  initialViewId?: string;
}

/**
 * Execute the bundle text the same way the experimental loader executes a
 * fetched experimental_app file: a dynamic ES-module import whose `app`
 * export is the App instance (@data-root/loader load()). The Blob URL keeps
 * the execution local - the bundle never crosses the network.
 */
export async function executePreviewBundle(bundleText: string): Promise<App> {
  const blob = new Blob([bundleText], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  try {
    const mod: Record<string, unknown> = await import(/* @vite-ignore */ url);
    const candidate =
      (mod?.app as App | undefined) ??
      ((mod?.default as Record<string, unknown> | undefined)?.app as App | undefined) ??
      (mod?.default as App | undefined);

    if (
      !candidate ||
      typeof candidate !== "object" ||
      !Array.isArray((candidate as App).tables) ||
      !Array.isArray((candidate as App).views)
    ) {
      throw new Error(
        "Bundle did not yield an App instance: expected an `app` export with tables and views " +
          "(same shape the experimental loader consumes)"
      );
    }
    return candidate;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Fetch a bundle from bundleUrl, enforcing the same 5MB cap as bundleText. */
export async function fetchPreviewBundle(bundleUrl: string): Promise<string> {
  const response = await fetch(bundleUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch bundleUrl (HTTP ${response.status})`);
  }
  const text = await response.text();
  if (text.length > MAX_BUNDLE_TEXT_CHARS) {
    throw new Error(
      `Fetched bundle exceeds the ${MAX_BUNDLE_TEXT_CHARS / 1_000_000}MB cap (got ${text.length} chars)`
    );
  }
  return text;
}

const PREVIEW_ENDPOINT = "preview";

/** Synthetic, in-memory only - NOT a session, NOT persisted, NO token. */
const PREVIEW_USER: User = {
  id: "preview-user",
  name: "Preview",
  email: "preview@local",
  permissions: [],
};

function sanitizeIdSegment(raw: string | undefined, fallback: string): string {
  const cleaned = (raw ?? "").replace(/[^a-zA-Z0-9_-]/g, "-").replace(/^-+|-+$/g, "");
  return cleaned || fallback;
}

/**
 * Register the executed App in the core registry and wire LOCAL data drivers
 * for every table - the preview equivalent of what experimental_getApp +
 * core.loadApp do for a deployed app, with PocketBase fully out of the loop.
 *
 * Each render gets a fresh deploymentId (`preview<seq>`), so registry
 * entries, driver cache keys (getCacheKey includes deploymentId) and the
 * Local driver's IndexedDB store uuids never collide with a previous render
 * (hot reload re-seeds cleanly).
 */
export async function registerPreviewApp(
  app: App,
  seq: number,
  seedData?: Record<string, Array<Record<string, unknown>>>
): Promise<RegisteredPreview> {
  const appId = sanitizeIdSegment(app.id, "preview-app");
  const deploymentId = `preview${seq}`;
  const appSpecifier: AppSpecifier = { appId, deploymentId };

  // Force the data provider to Local on every table (contract section 4.2):
  // DriverService.add selects the driver class from table.type, so this is
  // the single lever that decides where data calls go. Remote providers
  // (PocketBase) are never honored in preview.
  for (const table of app.tables) {
    table.type = "Local";
  }

  const database_map = Object.fromEntries(
    app.tables.map((table) => [
      table.id,
      {
        type: "Local" as const,
        id: table.id,
        name: table.name ?? table.id,
        // Per-render store uuid: a re-render (same or new previewId) seeds a
        // fresh IndexedDB store instead of layering onto stale rows.
        uuid: `${appId}-${deploymentId}-${table.id}`,
        endpoint: PREVIEW_ENDPOINT,
        path: "",
      },
    ])
  ) as DatabaseMap;

  const manifest: Manifest = {
    appId,
    deploymentId,
    prefersExperimental: true,
    name: app.name ?? appId,
    description: "EigenApp preview (no session, local data only)",
  };

  // Same registry the experimental loader populates (AppsService.apps); the
  // subsequent core.getApp/getTables calls resolve from here, never hitting
  // the internal (PocketBase) driver.
  core.app.apps.push({ appSpecifier, app, database_map, manifest });

  // In-memory only; never core.save(), never core.fromToken (section 4.1).
  if (!core.user) {
    core.user = PREVIEW_USER;
  }

  // Registers the Local driver per table + event actions (same call the
  // experimental loader makes).
  await core.loadApp(appSpecifier);

  if (seedData) {
    await seedPreviewData(appSpecifier, app, seedData);
  }

  let initialViewId: string | undefined;
  if (app.defaultViewId) {
    try {
      initialViewId = await core.evaluateFormula<string>(app.defaultViewId, {
        appId,
        deploymentId,
      });
    } catch (error) {
      console.warn("[previewRuntime] defaultViewId formula failed; falling back:", error);
    }
  }
  if (!initialViewId) initialViewId = app.views[0]?.id;

  try {
    if (app.styles?.primaryColor) changeThemeColor(app.styles.primaryColor);
  } catch {
    // theme color is cosmetic; never fail the preview for it
  }

  return { appSpecifier, app, initialViewId };
}

/**
 * Seed rows straight through the registered Local driver (not core.add: the
 * data-service delta path is unnecessary for seeding and depends on more
 * core state than the preview should touch).
 */
async function seedPreviewData(
  appSpecifier: AppSpecifier,
  app: App,
  seedData: Record<string, Array<Record<string, unknown>>>
): Promise<void> {
  for (const [tableId, rows] of Object.entries(seedData)) {
    if (!app.tables.some((table) => table.id === tableId)) {
      console.warn(`[previewRuntime] seedData table "${tableId}" not in the app; skipping`);
      continue;
    }
    const driver = core.driver.get(appSpecifier, tableId);
    if (!driver) {
      console.warn(`[previewRuntime] No driver registered for table "${tableId}"; skipping seed`);
      continue;
    }
    for (const row of rows) {
      const withId = { id: typeof row.id === "string" && row.id ? row.id : uuidv4(), ...row };
      try {
        await driver.add(appSpecifier, tableId, withId as never);
      } catch (error) {
        // Partial seed failures degrade the demo data, not the render.
        console.warn(`[previewRuntime] Failed to seed row into "${tableId}":`, error);
      }
    }
  }
}

/**
 * Tear down a previously rendered preview before a re-render (hot reload):
 * unregister event actions and drop the registry entry. The per-render
 * deploymentId means stale driver connections can never be resolved again;
 * they are abandoned (bounded by the number of re-renders in a tab session).
 */
export async function teardownPreviewApp(appSpecifier: AppSpecifier): Promise<void> {
  try {
    await core.app.unload(appSpecifier);
  } catch (error) {
    console.warn("[previewRuntime] unload during teardown failed:", error);
  }
  core.app.apps = core.app.apps.filter(
    (entry) =>
      !(
        entry.appSpecifier.appId === appSpecifier.appId &&
        entry.appSpecifier.deploymentId === appSpecifier.deploymentId
      )
  );
}
